import React from 'react';
import { motion } from 'framer-motion';

/**
 * PreviewGenerator - Génère un mockup visuel de site web
 * basé sur les informations de l'entreprise
 */
const PreviewGenerator = ({ preview, onClose, onContact }) => {
  if (!preview) return null;

  const { companyName, logo, colors, heroTitle, heroSubtitle, tagline, services, sector } = preview;

  // Styles dynamiques basés sur les couleurs de l'entreprise
  const dynamicStyles = {
    primary: colors?.primary || '#2563eb',
    secondary: colors?.secondary || '#7c3aed',
    accent: colors?.accent || '#06b6d4'
  };

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.9 }}
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
      onClick={onClose}
    >
      <motion.div
        initial={{ y: 50 }}
        animate={{ y: 0 }}
        className="relative w-full max-w-4xl max-h-[90vh] overflow-hidden rounded-2xl shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header avec bouton fermer */}
        <div className="absolute top-4 right-4 z-20 flex gap-2">
          <button
            onClick={onClose}
            className="p-2 bg-black/50 hover:bg-black/70 rounded-full text-white transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Badge "Aperçu généré par IA" */}
        <div className="absolute top-4 left-4 z-20">
          <span className="px-3 py-1 bg-gradient-to-r from-purple-500 to-pink-500 text-white text-xs font-bold rounded-full flex items-center gap-1">
            <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
              <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
              <path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd" />
            </svg>
            Aperçu IA
          </span>
        </div>

        {/* Mockup du site */}
        <div className="bg-white overflow-y-auto max-h-[90vh]">
          {/* Browser Chrome */}
          <div className="bg-gray-100 px-4 py-2 flex items-center gap-2 border-b sticky top-0 z-10">
            <div className="flex gap-1.5">
              <div className="w-3 h-3 rounded-full bg-red-400"></div>
              <div className="w-3 h-3 rounded-full bg-yellow-400"></div>
              <div className="w-3 h-3 rounded-full bg-green-400"></div>
            </div>
            <div className="flex-1 ml-4">
              <div className="bg-white rounded-md px-3 py-1 text-sm text-gray-500 border max-w-md">
                www.{companyName.toLowerCase().replace(/\s+/g, '')}.fr
              </div>
            </div>
          </div>

          {/* Navigation */}
          <nav
            className="px-6 py-4 flex items-center justify-between border-b"
            style={{ backgroundColor: 'white' }}
          >
            {/* Logo */}
            <div className="flex items-center gap-3">
              {logo?.logoUrl ? (
                <img
                  src={logo.logoUrl}
                  alt={companyName}
                  className="h-10 w-auto object-contain"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'flex';
                  }}
                />
              ) : null}
              <div
                className={`h-10 w-10 rounded-lg flex items-center justify-center text-white font-bold ${logo?.logoUrl ? 'hidden' : 'flex'}`}
                style={{ background: `linear-gradient(135deg, ${dynamicStyles.primary}, ${dynamicStyles.secondary})` }}
              >
                {logo?.initials || companyName.slice(0, 2).toUpperCase()}
              </div>
              <span className="font-bold text-gray-800">{companyName}</span>
            </div>

            {/* Menu */}
            <div className="hidden md:flex items-center gap-6 text-sm text-gray-600">
              <span className="hover:text-gray-900 cursor-pointer">Accueil</span>
              <span className="hover:text-gray-900 cursor-pointer">Services</span>
              <span className="hover:text-gray-900 cursor-pointer">À propos</span>
              <span
                className="px-4 py-2 rounded-lg text-white font-medium cursor-pointer"
                style={{ backgroundColor: dynamicStyles.primary }}
              >
                Contact
              </span>
            </div>
          </nav>

          {/* Hero Section */}
          <section
            className="relative px-6 py-20 text-center text-white overflow-hidden"
            style={{
              background: `linear-gradient(135deg, ${dynamicStyles.primary}, ${dynamicStyles.secondary})`
            }}
          >
            {/* Background Pattern */}
            <div className="absolute inset-0 opacity-10">
              <div className="absolute top-10 left-10 w-32 h-32 border-2 border-white rounded-full"></div>
              <div className="absolute bottom-10 right-10 w-48 h-48 border-2 border-white rounded-full"></div>
              <div className="absolute top-1/2 left-1/4 w-20 h-20 border-2 border-white rounded-full"></div>
            </div>

            <div className="relative z-10 max-w-3xl mx-auto">
              <span
                className="inline-block px-4 py-1 rounded-full text-sm font-medium mb-6"
                style={{ backgroundColor: 'rgba(255,255,255,0.2)' }}
              >
                {sector}
              </span>
              <h1 className="text-4xl md:text-5xl font-bold mb-4">
                {heroTitle}
              </h1>
              <p className="text-xl opacity-90 mb-8">
                {heroSubtitle}
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <button
                  className="px-6 py-3 bg-white rounded-lg font-medium transition-transform hover:scale-105"
                  style={{ color: dynamicStyles.primary }}
                >
                  Découvrir nos services
                </button>
                <button className="px-6 py-3 border-2 border-white rounded-lg font-medium hover:bg-white/10 transition-colors">
                  Nous contacter
                </button>
              </div>
            </div>
          </section>

          {/* Services Section */}
          <section className="px-6 py-16 bg-gray-50">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-2xl font-bold text-center text-gray-800 mb-2">
                Nos Services
              </h2>
              <p className="text-gray-500 text-center mb-10">{tagline}</p>

              <div className="grid md:grid-cols-3 gap-6">
                {services?.map((service, index) => (
                  <div
                    key={index}
                    className="bg-white p-6 rounded-xl shadow-sm hover:shadow-md transition-shadow border border-gray-100"
                  >
                    <div
                      className="w-12 h-12 rounded-lg flex items-center justify-center text-white mb-4"
                      style={{ backgroundColor: dynamicStyles.accent }}
                    >
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <h3 className="font-bold text-gray-800 mb-2">{service}</h3>
                    <p className="text-sm text-gray-500">
                      Solution personnalisée pour répondre à vos besoins spécifiques.
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* CTA Section */}
          <section
            className="px-6 py-12 text-center text-white"
            style={{
              background: `linear-gradient(135deg, ${dynamicStyles.secondary}, ${dynamicStyles.primary})`
            }}
          >
            <h2 className="text-2xl font-bold mb-2">Prêt à transformer votre présence en ligne ?</h2>
            <p className="opacity-90 mb-6">Obtenez un site comme celui-ci pour votre entreprise</p>
            <button
              onClick={onContact}
              className="px-8 py-3 bg-white rounded-lg font-bold transition-transform hover:scale-105"
              style={{ color: dynamicStyles.primary }}
            >
              Demander un devis gratuit
            </button>
          </section>

          {/* Footer Preview */}
          <footer className="px-6 py-8 bg-gray-900 text-gray-400 text-sm">
            <div className="max-w-4xl mx-auto flex flex-wrap justify-between items-center gap-4">
              <div className="flex items-center gap-2">
                <div
                  className="h-8 w-8 rounded flex items-center justify-center text-white text-xs font-bold"
                  style={{ background: `linear-gradient(135deg, ${dynamicStyles.primary}, ${dynamicStyles.secondary})` }}
                >
                  {logo?.initials || companyName.slice(0, 2).toUpperCase()}
                </div>
                <span>{companyName}</span>
              </div>
              <div className="flex gap-6">
                <span>Accueil</span>
                <span>Services</span>
                <span>Contact</span>
              </div>
            </div>
          </footer>
        </div>

        {/* CTA Bottom Bar */}
        <div className="bg-gradient-to-r from-[var(--primary)] to-[var(--secondary)] px-6 py-4 flex flex-wrap items-center justify-between gap-4">
          <div className="text-white">
            <p className="font-bold">Vous aimez ce que vous voyez ?</p>
            <p className="text-sm opacity-80">Olney Tech peut créer ce site pour vous !</p>
          </div>
          <button
            onClick={onContact}
            className="px-6 py-2 bg-white text-[var(--primary)] font-bold rounded-lg hover:scale-105 transition-transform"
          >
            Obtenir ce site
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default PreviewGenerator;
